import { CommonModule } from '@angular/common';
import {
	Component,
	DestroyRef,
	Signal,
	computed,
	HostListener,
	inject,
	OnInit,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { filter } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import {
	FeatureSettingsService,
	VbLxpFooterComponent,
	VbLxpHeaderComponent,
	VbLxpLayoutSelectorComponent,
	VbLxpNotificationComponent,
	VbLxpThemeToggleComponent,
	Notification,
	PanelService,
	RaceClassComponent,
	RaceSessionService,
	LivestreamViewService,
	VbLxpSwitchComponent,
	VbLxpRaceFlagIndicatorComponent,
	TimespanPipe,
	ThemeService,
	VideoLayout,
	VideoLayoutService,
	WebSocketService,
	WindowSizeService,
} from '@vbrick/vb-lxp-lib';

const LAYOUT_SVG_MAP: Record<VideoLayout, string> = {
	[VideoLayout.SINGLE]: 'assets/svg/1x1.svg',
	[VideoLayout.SIDE_BY_SIDE]: 'assets/svg/1x2.svg',
	[VideoLayout.LARGE_LEFT]: 'assets/svg/lg-left.svg',
	[VideoLayout.LARGE_RIGHT]: 'assets/svg/lg-right.svg',
	[VideoLayout.GRID_2X2]: 'assets/svg/2x2.svg',
	[VideoLayout.STACKED]: 'assets/svg/2x1.svg',
};

@Component({
	selector: 'app-root',
	imports: [
		CommonModule,
		RouterModule,
		TimespanPipe,
		VbLxpHeaderComponent,
		VbLxpFooterComponent,
		VbLxpThemeToggleComponent,
		VbLxpLayoutSelectorComponent,
		VbLxpSwitchComponent,
		RaceClassComponent,
		VbLxpRaceFlagIndicatorComponent,
		VbLxpNotificationComponent,
	],
	templateUrl: './app.html',
	styleUrl: './app.css',
	encapsulation: ViewEncapsulation.None,
	host: {
		'(document:click)': 'onDocumentClick($event)',
	},
})
export class App implements OnInit {
	public themeService = inject(ThemeService);
	public layoutService = inject(VideoLayoutService);
	public panelService = inject(PanelService);
	public windowSizeService = inject(WindowSizeService);
	public raceSessionService = inject(RaceSessionService);
	public featureSettingsService = inject(FeatureSettingsService);
	public livestreamViewService = inject(LivestreamViewService);
	public webSocketService = inject(WebSocketService);
	public router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);

	private featureSettings = toSignal(this.featureSettingsService.featureSettings$);

	protected readonly VideoLayout = VideoLayout;
	protected readonly layoutSvgMap = LAYOUT_SVG_MAP;
	protected activeLayoutSvg = '';
	protected readonly title = signal('sro');
	protected readonly footerImages = computed<{ src: string; link: string }[]>(() => {
		const gpxLogo = this.themeService.isDarkMode() ? 'assets/GPX.png' : 'assets/gpx-light.png';
		const awsLogo = this.themeService.isDarkMode() ? 'assets/AWS.png' : 'assets/AWS-white.png';

		return [
			{
				src: 'assets/Vbrick.png',
				link: 'https://vbrick.com/live-experience-platform/?utm_source=platform&utm_medium=banner&utm_campaign=sro&utm_content=&utm_term=',
			},
			{ src: gpxLogo, link: 'https://gpxstream.com/' },
			{ src: awsLogo, link: 'https://aws.amazon.com/' },
		];
	});

	isDropdownOpen = signal(false);
	isMobile = signal(false);
	isTimingActive = toSignal(this.panelService.getPanelState('timing'));
	isMapActive = toSignal(this.panelService.getPanelState('map'));
	isChatActive = toSignal(this.panelService.getPanelState('chat'));
	public isSessionArchived = toSignal(this.raceSessionService.isSessionArchived$);
	public raceInfo = toSignal(this.raceSessionService.raceInfo$);
	public raceStatus = toSignal(this.raceSessionService.raceStatus$);
	public timeStatus = toSignal(this.raceSessionService.timeStatus$);
	public raceClasses = toSignal(this.raceSessionService.raceClasses$, { initialValue: [] });
	public navigationEnd = toSignal(
		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
	);

	public currentNotification = computed<Notification | null>(() => {
		const msg = this.featureSettings()?.notificationMessage?.trim();
		return msg ? { type: 'info', message: msg } : null;
	});

	public isAdminRoute = computed(() => {
		this.navigationEnd();
		return this.router.url.startsWith('/admin') || this.router.url.startsWith('/login');
	});

	public controlVisibilities: Signal<any> = computed(() => {
		this.navigationEnd();
		let route = this.router.routerState.root;

		while (route.firstChild) {
			route = route.firstChild;
		}

		return route.snapshot.data;
	});

	private readonly MOBILE_BREAKPOINT = 768;
	private desktopLayout: VideoLayout | null = null;

	ngOnInit() {
		// Initialize SSE connection for public project (no auth required)
		this.webSocketService.connect();

		this.checkScreenSize();
		this.layoutService.activeLayout$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((activeLayout) => {
				this.activeLayoutSvg = LAYOUT_SVG_MAP[activeLayout];
				this.desktopLayout = activeLayout;
			});
	}

	@HostListener('window:resize')
	onResize() {
		this.checkScreenSize();
	}

	private checkScreenSize() {
		const wasMobile = this.isMobile();
		const nowMobile = window.innerWidth < this.MOBILE_BREAKPOINT;
		this.isMobile.set(nowMobile);
		this.windowSizeService.updateWindowSize();

		if (nowMobile && !wasMobile) {
			// Entering mobile: save current layout and force single
			this.layoutService.setLayout(VideoLayout.SINGLE);
		} else if (!nowMobile && wasMobile && this.desktopLayout !== null) {
			// Leaving mobile: restore previous layout
			this.layoutService.setLayout(this.desktopLayout);
			this.desktopLayout = null;
		}
	}

	toggleDropdown(event: Event) {
		event.stopPropagation();
		this.isDropdownOpen.update((open) => !open);
	}

	onDocumentClick(_event: Event) {
		this.isDropdownOpen.set(false);
	}

	onThemeToggle() {
		this.themeService.toggleDarkMode();
	}

	toggleTiming() {
		this.panelService.togglePanel('timing');
	}

	toggleChat() {
		this.panelService.togglePanel('chat');
	}

	toggleMap() {
		this.panelService.togglePanel('map');
	}

	onClassSelectionChange(selectedClasses: string[]) {
		this.raceSessionService.updateSelectedClasses(selectedClasses);
	}
}
